import React, { useState } from "react";
import {
  Button,
  Card,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Drawer,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  EditOutlined,
  CarOutlined,
  SearchOutlined,
  RollbackOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchVehicles_api,
  AddVehicle_api,
  UpdateVehicle_api,
  FetchVTMMappings_api,
  FetchVehicleColors_api,
  UpdateVehicleReturnStatus_api,
} from "../../Api/Vehicles/vehicle_api";
import { ImageProcessing } from "../../Api/ImageProccessing/ImageProccessing";
import { useAuthChecker } from "../../Context";
import { truncate } from "../../lib";
import { FetchDealers_api } from "../../Api";

const { Option } = Select;

export const ManageVehicle = () => {
  const { user } = useAuthChecker();
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [gatePassModalOpen, setGatePassModalOpen] = useState(false);
  const [gatePassImage, setGatePassImage] = useState<File | null>(null);
  const [processingImage, setProcessingImage] = useState(false);
  const [extractedVehicles, setExtractedVehicles] = useState<any[]>([]);
  const [bulkDealerId, setBulkDealerId] = useState<number | null>(null);

  // Bulk Edit State
  const [bulkVehicles, setBulkVehicles] = useState<any[]>([]);
  const [bulkEditIndex, setBulkEditIndex] = useState<number | null>(null);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [duplicateVehicles, setDuplicateVehicles] = useState<any[]>([]);
  const [bulkForm] = Form.useForm();

  // Return Vehicle State
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returningVehicle, setReturningVehicle] = useState<any>(null);
  const [returnForm] = Form.useForm();

  // View Return Reason State
  const [viewReasonModalOpen, setViewReasonModalOpen] = useState(false);
  const [viewingVehicle, setViewingVehicle] = useState<any>(null);

  // Pagination & Search State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [filterEngine, setFilterEngine] = useState("");
  const [filterChassis, setFilterChassis] = useState("");
  const [filterDealer, setFilterDealer] = useState<string | undefined>(
    undefined,
  );
  const [filterDate, setFilterDate] = useState<string | undefined>(undefined);

  // Queries
  const {
    data: vehicleData,
    refetch: refetchVehicles,
    isFetching: isFetchingVehicles,
  } = useQuery({
    queryKey: [
      "vehicles",
      page,
      pageSize,
      searchText,
      filterEngine,
      filterChassis,
      filterDealer,
      filterDate,
    ],
    queryFn: () =>
      FetchVehicles_api(page, pageSize, {
        search: searchText,
        engine_no: filterEngine,
        chassis_no: filterChassis,
        dealer_id: filterDealer,
        instock_date: filterDate,
      }),
  });

  const {
    data: dealerData,
    refetch: refetchDealer,
    isFetching: isFetchingDealers,
  } = useQuery({
    queryKey: ["dealers", page, pageSize, searchText],
    queryFn: () => FetchDealers_api(page, pageSize, searchText),
  });

  const { data: vtmData } = useQuery({
    queryKey: ["vtm_mappings"],
    queryFn: FetchVTMMappings_api,
  });

  const { data: colorsData } = useQuery({
    queryKey: ["vehicle_colors"],
    queryFn: FetchVehicleColors_api,
  });

  // Handlers
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldValue("instock_date", dayjs());
    setBulkVehicles([]);
    setModalOpen(true);
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      instock_date: record.instock_date ? dayjs(record.instock_date) : null,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        instock_date: values.instock_date
          ? values.instock_date.format("YYYY-MM-DD")
          : null,
        id: editingRecord?.id,
        userId: user?.userId,
      };
      const res = editingRecord
        ? await UpdateVehicle_api(payload)
        : await AddVehicle_api(payload);
      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);

        if (res.failedData && res.failedData.length > 0) {
          setBulkVehicles(res.failedData);
          setDuplicateVehicles(res.failedData);
        } else {
          setModalOpen(false);
          refetchVehicles();
        }
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mapVehicleData = (data: any, imageName: string = "") => {
    let mappedData = {
      ...data,
      gate_pass_image: imageName,
    };

    if (data.color && colorsData?.data) {
      const matchedColor = colorsData.data.find(
        (c: any) =>
          c.color.toLowerCase().includes(data.color.toLowerCase()) ||
          data.color.toLowerCase().includes(c.color.toLowerCase()),
      );
      if (matchedColor) {
        mappedData.v_color = matchedColor.id;
        mappedData.color_name = matchedColor.color;
      }
    }

    if ((data.vehicle_type || data.model || data.model_desc) && vtmData?.data) {
      const searchType = (data.vehicle_type || "").toLowerCase();
      const searchModel = (data.model || "").toLowerCase();
      const searchDesc = (data.model_desc || "").toLowerCase();

      const matchedVtm = vtmData.data.find((item: any) => {
        const type = (item.v_type || "").toLowerCase();
        const modal = (item.v_modal || "").toLowerCase();

        const typeMatch =
          searchType &&
          (type.includes(searchType) || searchType.includes(type));
        const modelMatch =
          searchModel &&
          (modal.includes(searchModel) || searchModel.includes(modal));
        const descMatch =
          searchDesc &&
          (modal.includes(searchDesc) || searchDesc.includes(modal));

        return typeMatch || modelMatch || descMatch;
      });

      if (matchedVtm) {
        mappedData.vehicle_type_has_vehicle_modal_id = matchedVtm.id;
        mappedData.v_type = matchedVtm.v_type;
        mappedData.v_modal = matchedVtm.v_modal;
        mappedData.price = matchedVtm.price;
      }
    }
    return mappedData;
  };

  const handleGatePassProcess = async () => {
    if (!gatePassImage) {
      message.error("Please select an image first");
      return;
    }

    setProcessingImage(true);
    try {
      // const data = {
      //   status: 200,
      //   result: {
      //     candidates: [
      //       {
      //         content: {
      //           parts: [
      //             {
      //               text: '{"gate_pass_no": "LPSMF12026011600846", "issued_date": "16\/01\/2026", "from": "MFI", "to": "NLY-(AC2023120953) - SRILAND BAJAJ", "driver_name": "MR PRASANGA", "carrier_no": "41-1934", "vehicles": [{"workshop_no": "LO1372025090014-0201", "vehicle_type": "Bike", "engine_number": "PFXW5G79203", "chassis_number": "MD2B37AX85WF54789", "model_desc": "CT 100 ES, 45, EBONY BLACK BLUE", "model": "CT 100 ES", "color": "EBONY BLACK BLUE", "other_ids": null}, {"workshop_no": "LO1372025100017-0133", "vehicle_type": "Bike", "engine_number": "PFXWSH81523", "chassis_number": "MD2B37AX18WH23065", "model_desc": "CT 100 ES, 4S, EBONY BLACK BLUE", "model": "CT 100 ES", "color": "EBONY BLACK BLUE", "other_ids": null}, {"workshop_no": "LO1372025100019-0252", "vehicle_type": "Bike", "engine_number": "PFXWSH81318", "chassis_number": "MD2B37AX0SWH23736", "model_desc": "CT 100 ES, 4S, EBONY BLACK BLUE", "model": "CT 100 ES", "color": "EBONY BLACK BLUE", "other_ids": null}, {"workshop_no": "LO1372025100019-0255", "vehicle_type": "Bike", "engine_number": "PFXWSH81320", "chassis_number": "MD2B37AX6SWH23739", "model_desc": "CT 100 ES, 4S, EBONY BLACK BLUE", "model": "CT 100 ES", "color": "EBONY BLACK BLUE", "other_ids": null}, {"workshop_no": "LO1372025100072-0104", "vehicle_type": "Bike", "engine_number": "PDXCSH88384", "chassis_number": "MD2B54DX4SCH96954", "model_desc": "PULSAR N160 FI DC ABS, BROOKLYN BLACK", "model": "PULSAR N160 FI DC ABS", "color": "BROOKLYN BLACK", "other_ids": null}]}',
      //             },
      //           ],
      //           role: "model",
      //         },
      //         finishReason: "STOP",
      //         index: 0,
      //       },
      //     ],
      //     usageMetadata: {
      //       promptTokenCount: 645,
      //       candidatesTokenCount: 716,
      //       totalTokenCount: 2654,
      //       cachedContentTokenCount: 512,
      //       promptTokensDetails: [
      //         { modality: "TEXT", tokenCount: 387 },
      //         { modality: "DOCUMENT", tokenCount: 258 },
      //       ],
      //       cacheTokensDetails: [
      //         { modality: "DOCUMENT", tokenCount: 205 },
      //         { modality: "TEXT", tokenCount: 307 },
      //       ],
      //       thoughtsTokenCount: 1293,
      //     },
      //     modelVersion: "gemini-2.5-flash",
      //     responseId: "v9OBaZvxDqi1xN8PwNnUyA0",
      //   },
      //   imageName: "uploaded_file_34488_1.pdf",
      // };

      const response = await ImageProcessing(gatePassImage, "gate_pass");
      //const response = data;
      const imageName = response.imageName || "";

      const candidates = response.result?.candidates;
      if (candidates && candidates.length > 0) {
        let text = candidates[0].content.parts[0].text;
        text = text
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();

        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.error("Failed to parse JSON:", text);
          message.error("Failed to parse extracted data.");
          return;
        }

        if (
          data.vehicles &&
          Array.isArray(data.vehicles) &&
          data.vehicles.length > 0
        ) {
          const mappedVehicles = data.vehicles.map((v: any) =>
            mapVehicleData(v, imageName),
          );
          setExtractedVehicles(mappedVehicles);
          setBulkVehicles(mappedVehicles);
          setGatePassModalOpen(false);
          setModalOpen(true);
          setGatePassImage(null);
          message.success(`Extracted ${mappedVehicles.length} vehicles.`);
        } else {
          const singleVehicle = {
            engine_number: data.engine_number,
            chassis_number: data.chassis_number,
            color: data.color,
            model: data.model,
            vehicle_type: data.vehicle_type,
            model_desc: data.model_desc,
          };

          if (singleVehicle.engine_number || singleVehicle.chassis_number) {
            const mapped = mapVehicleData(singleVehicle, imageName);
            setEditingRecord(null);
            form.setFieldsValue(mapped);
            setGatePassModalOpen(false);
            setModalOpen(true);
            setGatePassImage(null);
            message.success("Vehicle data populated.");
          } else {
            message.warning("No vehicle data found in response.");
          }
        }
      } else {
        message.error("Could not extract data from the image.");
      }
    } catch (error) {
      console.error("Image processing error:", error);
      message.error("Failed to process image. Please try again.");
    } finally {
      setProcessingImage(false);
    }
  };

  const fillFormWithVehicle = (data: any) => {
    form.setFieldsValue({
      engine_number: data.engine_number,
      chassis_number: data.chassis_number,
      instock_date: dayjs(),
    });

    if (data.color && colorsData?.data) {
      const matchedColor = colorsData.data.find(
        (c: any) =>
          c.color.toLowerCase().includes(data.color.toLowerCase()) ||
          data.color.toLowerCase().includes(c.color.toLowerCase()),
      );
      if (matchedColor) {
        form.setFieldValue("v_color", matchedColor.id);
      }
    }

    if ((data.vehicle_type || data.model || data.model_desc) && vtmData?.data) {
      const searchType = (data.vehicle_type || "").toLowerCase();
      const searchModel = (data.model || "").toLowerCase();
      const searchDesc = (data.model_desc || "").toLowerCase();

      const matchedVtm = vtmData.data.find((item: any) => {
        const type = (item.v_type || "").toLowerCase();
        const modal = (item.v_modal || "").toLowerCase();

        const typeMatch =
          searchType &&
          (type.includes(searchType) || searchType.includes(type));
        const modelMatch =
          searchModel &&
          (modal.includes(searchModel) || searchModel.includes(modal));
        const descMatch =
          searchDesc &&
          (modal.includes(searchDesc) || searchDesc.includes(modal));

        return typeMatch || modelMatch || descMatch;
      });

      if (matchedVtm) {
        form.setFieldValue("vehicle_type_has_vehicle_modal_id", matchedVtm.id);
      }
    }

    message.success("Form populated with vehicle data.");
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60 },
    {
      title: "Vehicle Details",
      key: "details",
      render: (record: any) => (
        <div>
          <div className="font-semibold text-blue-600">
            {record.v_type} - {record.v_modal}
          </div>
          <div className="text-xs text-gray-500">
            Color: {record.color_name}
          </div>
          <div className="text-xs text-gray-400">
            Added: {record.added_date?.split(" ")[0]}
          </div>
        </div>
      ),
    },
    {
      title: "In-Stock",
      dataIndex: "instock_date",
      key: "instock_date",
      render: (val: any) => (val ? val.split(" ")[0] : "-"),
    },
    { title: "Engine No", dataIndex: "engine_number", key: "engine_number" },
    { title: "Chassis No", dataIndex: "chassis_number", key: "chassis_number" },
    {
      title: "Dealer",
      key: "dealer_name",
      render: (record: any) => <div>{record.dealer_name}</div>,
    },
    {
      title: "Status",
      key: "status",
      render: (record: any) => (
        <div>
          {record.status === "available" && <Tag color="green">Available</Tag>}
          {record.status === "sold" && (
            <>
              <Tag color="red">Sold</Tag>
              <div className="text-xs text-gray-500">
                {record.sold_date?.split(" ")[0]}
              </div>
            </>
          )}
          {record.status == "returnd" && (
            <>
              <Tag color="orange">Returned</Tag>
              <div className="text-xs text-gray-500">
                {record.return_date?.split(" ")[0]}
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      title: "PriceInfo (Rs:)",
      dataIndex: "price",
      key: "price",
      render: (val: any) => `${parseFloat(val || 0).toLocaleString()}`,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (record: any) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.status == "sold" || record.status == "returnd"}
          ></Button>
          {record.status == "available" && (
            <Button
              size="small"
              icon={<RollbackOutlined />}
              onClick={() => {
                setReturningVehicle(record);
                returnForm.resetFields();
                returnForm.setFieldValue("return_date", dayjs());
                setReturnModalOpen(true);
              }}
              danger
            ></Button>
          )}
          {record.status == "returnd" && (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setViewingVehicle(record);
                setViewReasonModalOpen(true);
              }}
              type="link"
            ></Button>
          )}
        </Space>
      ),
    },
  ];

  const columnsVehicle = [
    { title: "Ws. No", dataIndex: "workshop_no", width: 60 },
    { title: "Engine Value", dataIndex: "engine_number" },
    { title: "Chassis Value", dataIndex: "chassis_number" },
    {
      title: "Vehicle Model",
      dataIndex: "v_modal",
      render: (val, record) => (
        <span className="font-semibold">
          {record.v_type} {val}
        </span>
      ),
    },
    {
      title: "In-Stock",
      dataIndex: "instock_date",
      render: (val: any) => (val ? val : new Date().toLocaleDateString()),
    },
    {
      title: "Color",
      dataIndex: "color_name",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "Error",
      dataIndex: "error",
      render: (val: any) =>
        val ? (
          <Tag color="red" className="text-wrap">
            {truncate(val, 20)}
          </Tag>
        ) : null,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setBulkEditIndex(index);
              bulkForm.setFieldsValue({
                ...record,
                dealer_id: record.dealer_id,
                vehicle_type_has_vehicle_modal_id:
                  record.vehicle_type_has_vehicle_modal_id,
                v_color: record.v_color,
                instock_date: record.instock_date
                  ? dayjs(record.instock_date)
                  : dayjs(),
              });
              setBulkEditModalOpen(true);
            }}
          />
          <Button
            icon={<PlusOutlined className="rotate-45" />}
            danger
            size="small"
            onClick={() => {
              const newBulk = [...bulkVehicles];
              newBulk.splice(index, 1);
              setBulkVehicles(newBulk);
              if (newBulk.length === 0) {
                message.info(
                  "All bulk items removed. Switching to single entry.",
                );
              }
            }}
          />
        </Space>
      ),
    },
  ];

  const columnExtractVehicle = [
    { title: "Item", dataIndex: "item_no", width: 60 },
    { title: "Engine No", dataIndex: "engine_number" },
    { title: "Chassis No", dataIndex: "chassis_number" },
    {
      title: "Model",
      dataIndex: "model",
      render: (t, r) => r.model || r.model_desc,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          onClick={() => {
            fillFormWithVehicle(record);
            setGatePassModalOpen(false);
            setGatePassImage(null);
            setExtractedVehicles([]);
          }}
        >
          Select
        </Button>
      ),
    },
  ];

  const handleReturnSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: returningVehicle?.id,
        return_date: values.return_date.format("YYYY-MM-DD HH:mm:ss"),
        return_reason: values.return_reason,
        userId: user?.userId,
      };

      const res = await UpdateVehicleReturnStatus_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        setReturnModalOpen(false);
        returnForm.resetFields();
        setReturningVehicle(null);
        refetchVehicles();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.error("Return vehicle error:", error);
      message.error("Failed to return vehicle. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async () => {
    try {
      // Add user ID to each vehicle
      const payload = bulkVehicles.map((v) => ({
        ...v,
        dealer_id: bulkDealerId,
        instock_date: v.instock_date
          ? dayjs(v.instock_date).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        userId: user?.userId,
      }));

      const res = await AddVehicle_api(payload);
      if (res.isDataAdded) {
        message.success(res.msg);
        refetchVehicles();

        if (res.failedData && res.failedData.length > 0) {
          setBulkVehicles(res.failedData);
        } else {
          setModalOpen(false);
          setBulkVehicles([]);
        }
      } else {
        if (res.failedData && res.failedData.length > 0) {
          setBulkVehicles(res.failedData);
        }
        message.error(res.msg);
      }
    } catch (error) {
      console.error("Bulk save error", error);
      message.error("Failed to save vehicles");
    } finally {
      setSubmitting(false);
      setGatePassModalOpen(false);
      setGatePassImage(null);
      setExtractedVehicles([]);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Vehicles</h1>
          <p className="text-gray-500">Inventory and stock management</p>
        </div>
        <div className="flex gap-4">
          <Input
            placeholder="Search vehicles..."
            prefix={<SearchOutlined />}
            className="w-64"
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
          >
            Add New Vehicle
          </Button>
        </div>
      </div>

      <Card className="shadow-sm rounded-xl border-none mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Engine Number
            </p>
            <Input
              placeholder="Filter by Engine No..."
              value={filterEngine}
              onChange={(e) => {
                setFilterEngine(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Chassis Number
            </p>
            <Input
              placeholder="Filter by Chassis No..."
              value={filterChassis}
              onChange={(e) => {
                setFilterChassis(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Dealer
            </p>
            <Select
              placeholder="Filter by Dealer..."
              className="w-full"
              showSearch
              allowClear
              optionFilterProp="children"
              value={filterDealer}
              onChange={(val) => {
                setFilterDealer(val);
                setPage(1);
              }}
            >
              {dealerData?.dealerData?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              In-Stock Date
            </p>
            <DatePicker
              placeholder="Filter by Date..."
              className="w-full"
              value={filterDate ? dayjs(filterDate) : null}
              onChange={(date, dateString) => {
                setFilterDate((dateString as string) || undefined);
                setPage(1);
              }}
            />
          </div>
        </div>
      </Card>

      <Card className="shadow-sm rounded-xl overflow-hidden border-none">
        <Table
          loading={isFetchingVehicles}
          dataSource={vehicleData?.data || []}
          columns={columns}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: vehicleData?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          className="overflow-x-auto"
        />
      </Card>

      <Drawer
        title={
          <div className="flex justify-between items-center">
            <p className="my-0 py-0">
              {editingRecord ? "Edit Vehicle" : "Add New Vehicle"}
            </p>

            <Button
              type="primary"
              size="small"
              onClick={() => setGatePassModalOpen(true)}
              className="px-3 py-4"
            >
              Process Gate pass
            </Button>
          </div>
        }
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={850}
      >
        {bulkVehicles.length > 0 ? (
          <div className="flex flex-col gap-4">
            <div className="bg-blue-50 p-4 rounded-md mb-2">
              <p className="font-semibold text-blue-800 m-0">
                Bulk Vehicle Entry
              </p>
              <p className="text-blue-600 text-sm m-0">
                Review the extracted data below. Edit any incorrect details
                before saving.
              </p>
            </div>

            <Select
              placeholder="Select Dealer"
              showSearch
              optionFilterProp="children"
              loading={isFetchingDealers}
              onChange={(value) => setBulkDealerId(value)}
            >
              {dealerData?.dealerData?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>

            {duplicateVehicles.length > 0 && (
              <div className="bg-red-50 p-4 rounded-md mb-2 border border-red-200">
                <p className="font-semibold text-red-800 m-0">
                  Action Required
                </p>
                <p className="text-red-700 text-sm m-0">
                  Some vehicles could not be saved due to errors (e.g.
                  duplicates). Please fix the highlighted items and try again.
                </p>
              </div>
            )}

            <Table
              dataSource={bulkVehicles}
              rowKey={(r) => r.chassis_number || Math.random()}
              pagination={false}
              size="small"
              className="overflow-x-auto"
              columns={columnsVehicle}
            />

            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button onClick={() => setBulkVehicles([])} danger>
                Discard All
              </Button>
              <Button
                type="primary"
                size="middle"
                onClick={async () => {
                  setSubmitting(true);
                  handleBulkSubmit();
                }}
                disabled={bulkVehicles.length === 0 || bulkDealerId === null}
                loading={submitting}
              >
                Save All Vehicles
              </Button>
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Vehicle Type & Model"
                name="vehicle_type_has_vehicle_modal_id"
                rules={[
                  { required: true, message: "Please select vehicle mapping" },
                ]}
              >
                <Select
                  placeholder="Select Type - Model"
                  showSearch
                  optionFilterProp="children"
                >
                  {vtmData?.data?.map((item: any) => (
                    <Option key={item.id} value={item.id}>
                      {item.v_type} - {item.v_modal} (Rs.{" "}
                      {parseFloat(item.price).toLocaleString()})
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {!editingRecord && (
                <Form.Item
                  label="Dealer"
                  name="dealer_id"
                  rules={[{ required: true, message: "Please select dealer" }]}
                >
                  <Select
                    placeholder="Select Dealer"
                    showSearch
                    optionFilterProp="children"
                    loading={isFetchingDealers}
                  >
                    {dealerData?.dealerData?.map((item: any) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                label="Vehicle Color"
                name="v_color"
                rules={[{ required: true, message: "Please select color" }]}
              >
                <Select
                  placeholder="Select Color"
                  showSearch
                  optionFilterProp="children"
                >
                  {colorsData?.data?.map((color: any) => (
                    <Option key={color.id} value={color.id}>
                      {color.color}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="In-Stock Date"
                name="instock_date"
                rules={[{ required: true, message: "Please select date" }]}
              >
                <DatePicker className="w-full" />
              </Form.Item>

              <Form.Item
                label="Engine Number"
                name="engine_number"
                rules={[
                  { required: true, message: "Please enter engine number" },
                ]}
              >
                <Input placeholder="Enter Engine Number" />
              </Form.Item>

              <Form.Item
                label="Chassis Number"
                name="chassis_number"
                rules={[
                  { required: true, message: "Please enter chassis number" },
                ]}
              >
                <Input placeholder="Enter Chassis Number" />
              </Form.Item>

              <Form.Item name="gate_pass_image" hidden>
                <Input />
              </Form.Item>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<CarOutlined />}
              >
                {editingRecord ? "Update Vehicle" : "Save Vehicle"}
              </Button>
            </div>
          </Form>
        )}
      </Drawer>

      {/* Bulk Edit Modal */}
      <Modal
        title="Edit Vehicle Details"
        open={bulkEditModalOpen}
        onCancel={() => setBulkEditModalOpen(false)}
        onOk={() => {
          bulkForm.validateFields().then((values) => {
            if (bulkEditIndex !== null) {
              const newBulk = [...bulkVehicles];
              const existing = newBulk[bulkEditIndex];

              // Merge new values
              const updated = { ...existing, ...values };

              // Re-run helpers to get display names for color/model if changed
              if (values.v_color && colorsData?.data) {
                const c = colorsData.data.find(
                  (x: any) => x.id === values.v_color,
                );
                if (c) updated.color_name = c.color;
              }

              if (values.instock_date) {
                updated.instock_date = values.instock_date.format("YYYY-MM-DD");
              }

              if (values.vehicle_type_has_vehicle_modal_id && vtmData?.data) {
                const m = vtmData.data.find(
                  (x: any) => x.id === values.vehicle_type_has_vehicle_modal_id,
                );
                if (m) {
                  updated.v_type = m.v_type;
                  updated.v_modal = m.v_modal;
                }
              }

              newBulk[bulkEditIndex] = updated;
              setBulkVehicles(newBulk);
              setBulkEditModalOpen(false);
              message.success("Row updated");
            }
          });
        }}
      >
        <Form form={bulkForm} layout="vertical">
          <div className="grid grid-cols-1 gap-2">
            <Form.Item
              label="Vehicle Type & Model"
              name="vehicle_type_has_vehicle_modal_id"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                placeholder="Select Type - Model"
                showSearch
                optionFilterProp="children"
              >
                {vtmData?.data?.map((item: any) => (
                  <Option key={item.id} value={item.id}>
                    {item.v_type} - {item.v_modal}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="In-Stock Date"
              name="instock_date"
              rules={[{ required: true }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>

            <Form.Item
              label="Vehicle Color"
              name="v_color"
              rules={[{ required: true }]}
            >
              <Select showSearch optionFilterProp="children">
                {colorsData?.data?.map((color: any) => (
                  <Option key={color.id} value={color.id}>
                    {color.color}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Engine Number"
              name="engine_number"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Chassis Number"
              name="chassis_number"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Upload Gate Pass Image"
        open={gatePassModalOpen}
        onCancel={() => {
          setGatePassModalOpen(false);
          setGatePassImage(null);
          setExtractedVehicles([]);
        }}
        footer={null}
        width={800}
        className="z-[9999]"
      >
        {!extractedVehicles.length ? (
          <div className="p-4">
            <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center p-6 mb-4">
              <Input
                type="file"
                accept="image/*"
                className="mb-2"
                onChange={(e) =>
                  setGatePassImage(e.target.files ? e.target.files[0] : null)
                }
              />
              <p className="text-gray-500 text-sm">
                Upload a clear image of the gate pass to extract details.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                type="primary"
                onClick={handleGatePassProcess}
                loading={processingImage}
                disabled={!gatePassImage}
              >
                Process & Extract
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="mb-4 text-green-600 font-medium">
              Successfully extracted {extractedVehicles.length} vehicles. Click
              "Select" to fill the form.
            </p>
            <div className="max-h-96 overflow-y-auto hidden">
              <Table
                dataSource={extractedVehicles}
                rowKey="chassis_number"
                pagination={false}
                size="small"
                className="overflow-x-auto"
                columns={columnExtractVehicle}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => setExtractedVehicles([])}>
                Back to Upload
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Return Vehicle Modal */}
      <Modal
        title={`Return Vehicle - ${returningVehicle?.engine_number || ""}`}
        open={returnModalOpen}
        onCancel={() => {
          setReturnModalOpen(false);
          returnForm.resetFields();
          setReturningVehicle(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={returnForm}
          layout="vertical"
          onFinish={handleReturnSubmit}
          className="mt-4"
        >
          <Form.Item
            label="Return Date"
            name="return_date"
            rules={[{ required: true, message: "Please select return date" }]}
          >
            <DatePicker
              showTime
              className="w-full"
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>

          <Form.Item
            label="Return Reason"
            name="return_reason"
            rules={[
              { required: true, message: "Please enter return reason" },
              { min: 10, message: "Reason must be at least 10 characters" },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Please provide detailed reason for returning this vehicle..."
            />
          </Form.Item>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={() => {
                setReturnModalOpen(false);
                returnForm.resetFields();
                setReturningVehicle(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              danger
            >
              Confirm Return
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Return Reason Modal */}
      <Modal
        title="Vehicle Return Details"
        open={viewReasonModalOpen}
        onCancel={() => {
          setViewReasonModalOpen(false);
          setViewingVehicle(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setViewReasonModalOpen(false);
              setViewingVehicle(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        {viewingVehicle && (
          <div className="space-y-4">
            <div>
              <p className="text-gray-500 text-sm mb-1">Vehicle Details</p>
              <p className="font-semibold">
                {viewingVehicle.v_type} - {viewingVehicle.v_modal}
              </p>
              <p className="text-sm text-gray-600">
                Engine: {viewingVehicle.engine_number}
              </p>
              <p className="text-sm text-gray-600">
                Chassis: {viewingVehicle.chassis_number}
              </p>
            </div>

            <div className="border-t pt-4">
              <p className="text-gray-500 text-sm mb-1">Return Date</p>
              <p className="font-medium">
                {viewingVehicle.return_date
                  ? dayjs(viewingVehicle.return_date).format(
                      "MMMM D, YYYY [at] h:mm A",
                    )
                  : "N/A"}
              </p>
            </div>

            <div className="border-t pt-4">
              <p className="text-gray-500 text-sm mb-2">Return Reason</p>
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {viewingVehicle.reaturn_reson || "No reason provided"}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
